/**
 * POI address extraction helpers.
 *
 * Tiles sometimes expose address fields directly (e.g. `address`, `addr_*`)
 * and sometimes pack them into a single `tags` field (JSON or hstore-ish string).
 */

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

export const parseTagsValue = (value) => {
  if (!isNonEmptyString(value)) return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

export const extractAddressFromTags = (tagsVal) => {
  if (!tagsVal) return null;

  // tags is an object
  if (typeof tagsVal === 'object') {
    return (
      tagsVal.address ||
      tagsVal['addr:full'] ||
      tagsVal.full_address ||
      tagsVal.entity_address ||
      tagsVal.display_address ||
      null
    );
  }

  if (typeof tagsVal !== 'string') return null;
  const trimmed = tagsVal.trim();

  // JSON string (as text)
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    const parsed = parseTagsValue(trimmed);
    if (parsed && typeof parsed === 'object') {
      return (
        parsed.address ||
        parsed['addr:full'] ||
        parsed.full_address ||
        parsed.entity_address ||
        parsed.display_address ||
        null
      );
    }
  }

  // hstore-ish string: "key"=>"value", 'key'=>'value', key=>"value", key=>'value'
  const pick = (key) => {
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return (
      trimmed.match(new RegExp(`"${escaped}"\\s*=>\\s*"([^"]*)"`, 'i')) ||
      trimmed.match(new RegExp(`'${escaped}'\\s*=>\\s*'([^']*)'`, 'i')) ||
      trimmed.match(new RegExp(`${escaped}\\s*=>\\s*"([^"]*)"`, 'i')) ||
      trimmed.match(new RegExp(`${escaped}\\s*=>\\s*'([^']*)'`, 'i'))
    );
  };

  const addrFull = pick('addr:full');
  if (addrFull?.[1]) return addrFull[1];

  const address = pick('address');
  if (address?.[1]) return address[1];

  return null;
};

export const getPoiAddress = (properties) => {
  const props = properties || {};

  // Direct address-like properties (common)
  const direct =
    props.address ||
    props.full_address ||
    props.entity_address ||
    props.display_address ||
    props['addr:full'] ||
    props.addr_full ||
    null;
  if (isNonEmptyString(direct)) return direct.trim();

  // Structured addr_* fields (underscore style)
  const parts = [
    props.addr_housenumber,
    props.addr_street,
    props.addr_city,
    props.addr_district,
    props.addr_state,
    props.addr_postcode
  ].filter(isNonEmptyString);
  if (parts.length > 0) return parts.join(', ');

  // Structured addr:* fields (colon style)
  const partsColon = [
    props['addr:housenumber'],
    props['addr:street'],
    props['addr:city'],
    props['addr:district'],
    props['addr:state'],
    props['addr:postcode']
  ].filter(isNonEmptyString);
  if (partsColon.length > 0) return partsColon.join(', ');

  // Packed tags field
  const fromTags = extractAddressFromTags(parseTagsValue(props.tags));
  if (isNonEmptyString(fromTags)) return fromTags.trim();

  return '';
};


